/**
 * ScenarioIntro - Cinematic intro screen shown before a simulation starts.
 *
 * Displays:
 * - Environment context (time, place)
 * - AI character avatar + role
 * - Scenario description with progressive text reveal
 * - "Begin" button
 */

import React, { useEffect, useState } from 'react';
import { Play, Clock, MapPin, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SimulationAvatar, { AvatarEmotion } from './SimulationAvatar';

interface ScenarioIntroProps {
  title: string;
  instructions?: string;
  durationMinutes?: number | null;
  scenarioPrompt?: string;
  scenarioContext?: string;
  aiPersona?: string;
  aiRole?: string;
  blockConfig?: Record<string, any>;
  onStart: () => void;
}

// Extract scene setting from scenario prompt
function extractSceneSetting(scenarioPrompt?: string): { time?: string; place?: string; situation?: string } {
  if (!scenarioPrompt) return {};

  const text = scenarioPrompt.toLowerCase();
  const setting: { time?: string; place?: string; situation?: string } = {};

  // Try to detect time
  const timeMatches = text.match(/(sabah|öğlen|akşam|gece|morning|afternoon|evening|night|pazartesi|salı|çarşamba|perşembe|cuma|cumartesi|pazar|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/g);
  if (timeMatches && timeMatches.length > 0) {
    setting.time = timeMatches[0].charAt(0).toUpperCase() + timeMatches[0].slice(1);
  }

  // Try to detect place
  const placeMatches = text.match(/(ofis|office|toplantı odası|meeting room|mağaza|store|çağrı merkezi|call center|saha|field|fabrika|factory|restoran|restaurant)/g);
  if (placeMatches && placeMatches.length > 0) {
    setting.place = placeMatches[0].charAt(0).toUpperCase() + placeMatches[0].slice(1);
  }

  return setting;
}

const ScenarioIntro: React.FC<ScenarioIntroProps> = ({
  title,
  instructions,
  durationMinutes,
  scenarioPrompt,
  scenarioContext,
  aiPersona,
  aiRole,
  blockConfig,
  onStart,
}) => {
  const { t } = useTranslation();
  const [visibleLines, setVisibleLines] = useState(0);
  const [showButton, setShowButton] = useState(false);

  const sceneSetting = extractSceneSetting(scenarioPrompt);
  const personaName = blockConfig?.ai_persona_name || aiRole || t('flowInterview.aiCharacter');
  const personaDescription = aiPersona || '';

  // Progressive text reveal
  const introLines = [
    scenarioContext || scenarioPrompt || '',
  ].filter(Boolean);

  useEffect(() => {
    setVisibleLines(0);
    setShowButton(false);

    if (introLines.length === 0) {
      setShowButton(true);
      return;
    }

    const timers: number[] = [];
    introLines.forEach((_, index) => {
      timers.push(
        window.setTimeout(() => {
          setVisibleLines(index + 1);
          if (index === introLines.length - 1) {
            timers.push(
              window.setTimeout(() => setShowButton(true), 800)
            );
          }
        }, 600 + index * 1200)
      );
    });

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [scenarioPrompt, scenarioContext]);

  return (
    <div className="flex-1 flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
      <div className="max-w-2xl w-full">
        {/* Cinematic letterbox bars */}
        <div className="fixed top-0 left-0 right-0 h-12 bg-black z-50" />
        <div className="fixed bottom-0 left-0 right-0 h-12 bg-black z-50" />

        <div className="text-center space-y-6 py-8">
          {/* Title */}
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-indigo-400 font-medium">
              {t('flowInterview.simulationScenario')}
            </div>
            <h2 className="text-3xl font-bold text-white">{title}</h2>
          </div>

          {/* Scene setting badges */}
          {(sceneSetting.time || sceneSetting.place || durationMinutes) && (
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {sceneSetting.time && (
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-sm backdrop-blur-sm">
                  <Clock size={14} className="text-indigo-300" />
                  {sceneSetting.time}
                </div>
              )}
              {sceneSetting.place && (
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-sm backdrop-blur-sm">
                  <MapPin size={14} className="text-indigo-300" />
                  {sceneSetting.place}
                </div>
              )}
              {durationMinutes && (
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-sm backdrop-blur-sm">
                  <Clock size={14} className="text-indigo-300" />
                  {durationMinutes} {t('flowInterview.minutesShort')}
                </div>
              )}
            </div>
          )}

          {/* AI Character card */}
          <div className="flex flex-col items-center gap-3 py-4">
            <SimulationAvatar
              emotion="neutral"
              name={personaName}
              role={personaDescription.slice(0, 60) + (personaDescription.length > 60 ? '...' : '')}
              size="lg"
            />
          </div>

          {/* Progressive scenario text */}
          <div className="space-y-3 min-h-[80px]">
            {introLines.map((line, index) => (
              <div
                key={index}
                className={`text-gray-300 text-lg leading-relaxed transition-all duration-700 ${
                  index < visibleLines
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
              >
                {line}
              </div>
            ))}
          </div>

          {/* Instructions */}
          {instructions && visibleLines >= introLines.length && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-left transition-all duration-500"
              style={{ opacity: visibleLines >= introLines.length ? 1 : 0 }}
            >
              <div className="text-xs uppercase tracking-wider text-indigo-300 font-medium mb-2">
                {t('flowInterview.instructions')}
              </div>
              <div className="text-gray-300 text-sm whitespace-pre-wrap">{instructions}</div>
            </div>
          )}

          {/* Warning */}
          {visibleLines >= introLines.length && (
            <div className="text-xs text-amber-400/70 transition-opacity duration-500"
              style={{ opacity: visibleLines >= introLines.length ? 1 : 0 }}
            >
              {t('flowInterview.abandonWarning')}
            </div>
          )}

          {/* Begin button */}
          <div className={`transition-all duration-500 ${showButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <button
              onClick={onStart}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-indigo-500 text-white font-semibold text-lg hover:bg-indigo-600 transition-colors shadow-lg shadow-indigo-500/30"
            >
              <Play size={22} fill="white" />
              {t('flowInterview.beginSimulation')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioIntro;
